import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, TID, errMsg} from "@/lib/config";
import { toast } from "sonner";
import { Loader2, LogOut, Package, MessageSquare, Layers, CreditCard, DollarSign, RefreshCw, ExternalLink, Check, X, Copy, Ticket, FileText, Settings, Send, Mail, Lock, BarChart3, Download, RotateCcw, Scale, Newspaper, Edit, Trash2, Eye, Power, ShoppingBag, Users, Building2, MapPin, Star, Plus, Search, Phone, Wrench, CheckCircle2, AlertTriangle } from "lucide-react";
import { Navigate } from "react-router-dom";
import MediaManager from "@/pages/AdminMedia";
import PaymentsSetup from "@/pages/AdminPayments";
import CashFlow from "@/pages/AdminCashFlow";
import AdminShop from "@/pages/AdminShop";
import AnagraficaPanel from "@/pages/AdminAnagrafica";
import AdminReportEditor from "@/pages/AdminReportEditor";
import AdminSafety from "@/pages/AdminSafety";
import { marked } from "marked";
import DOMPurify from "dompurify";

export function AdminLogin() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("admin@gefion-evolution.it");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="pt-40 text-center font-mono-tech text-white">Caricamento...</div>;
  if (user) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Bentornato.");
    } catch (err) {
      toast.error(errMsg(err, "Credenziali non valide"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden md:block md:w-1/2 relative overflow-hidden bg-[#0a0a0a] blueprint-grid">
        <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_30%_50%,rgba(255,75,0,0.15),transparent_60%)]" />
        <div className="relative z-10 h-full flex flex-col justify-between p-16">
          <div className="font-display text-2xl"><img src="/assets/logo/gefion.png" alt="Gefion Evolution" className="h-10 w-auto object-contain brightness-0 invert" /></div>
          <div>
            <div className="font-mono-tech text-white mb-4">◐ Area riservata</div>
            <div className="font-display text-white text-6xl leading-[0.9]">Dashboard<br/>tecnica.</div>
            <div className="text-white mt-6 max-w-sm">Gestisci preventivi taglio laser, prezzi materiali e richieste di contatto.</div>
          </div>
          <div className="font-mono-tech text-white text-[10px]">v.01 · Uso interno</div>
        </div>
      </div>
      <div className="w-full md:w-1/2 flex items-center justify-center px-6 py-24">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="num-chip mb-8"><span className="dot" /> Accesso Amministratore</div>
          <div className="font-display text-4xl mb-10">Entra.</div>
          <div className="space-y-6">
            <div>
              <label className="field-label">Email</label>
              <input required type="email" data-testid={TID.adminEmail} className="field mt-3" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="field-label">Password</label>
              <input required type="password" data-testid={TID.adminPassword} className="field mt-3" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
          </div>
          <button disabled={submitting} data-testid={TID.adminSubmit} className="btn-primary w-full justify-center mt-10">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : "Accedi"}
          </button>
        </form>
      </div>
    </div>
  );
}

export function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const [tab, setTab] = useState("cash-flow");
  // Redirect technicians to anagrafica (they can't access cash-flow)
  useEffect(() => {
    if (user?.role === "technician" && tab === "cash-flow") setTab("anagrafica");
  }, [user?.role, tab]);
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [s, q, m, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/quotes"),
        api.get("/admin/materials"),
        api.get("/admin/contacts"),
      ]);
      setStats(s.data); setQuotes(q.data); setMaterials(m.data); setContacts(c.data);
    } catch (e) {
      toast.error("Errore nel caricamento");
    }
  };
  useEffect(() => { if (user) loadAll(); }, [user]);

  if (loading) return <div className="pt-40 text-center font-mono-tech text-white">Caricamento...</div>;
  if (!user) return <Navigate to="/admin" replace />;

  return (
    <div className="pt-24 pb-24">
      {/* Header bar */}
      <div className="border-b border-[#1a1a1a]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <div className="num-chip mb-4"><span className="dot" /> Dashboard Amministratore</div>
            <div className="font-display text-4xl md:text-6xl">Ciao, {user.email.split("@")[0]}.</div>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3">
            <button onClick={loadAll} className="btn-ghost"><RefreshCw size={14} /> Aggiorna</button>
            <button onClick={logout} data-testid={TID.adminLogout} className="btn-ghost"><LogOut size={14} /> Esci</button>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-8 grid grid-cols-2 md:grid-cols-5 gap-4 border-b border-[#1a1a1a]">
          {[
            ["Preventivi totali", stats.total_quotes, Layers],
            ["In revisione", stats.pending, Loader2],
            ["Approvati", stats.approved, Check],
            ["Pagati/In produzione", stats.paid, Package],
            ["Ricavo (€)", stats.revenue_eur.toFixed(2), DollarSign],
          ].map(([label, val, Icon]) => (
            <div key={label} className="border border-[#1a1a1a] p-5">
              <div className="flex items-center justify-between mb-3"><span className="font-mono-tech text-[10px] text-white">{label}</span><Icon size={14} className="text-[#ff4b00]" /></div>
              <div className="font-display text-3xl">{val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-10">
        <div className="flex gap-2 mb-8 border-b border-[#1a1a1a] flex-wrap">
          {(() => {
            const ALL_TABS = [
              ["cash-flow", "Cash Flow", "admin-tab-cash-flow"],
              ["shop", "Shop", "admin-tab-shop"],
              ["quotes", "Preventivi Laser", TID.adminTabQuotes],
              ["materials", "Materiali", TID.adminTabMaterials],
              ["tickets", "Ticket Assistenza", "admin-tab-tickets"],
              ["contracts", "Richieste Contratto", "admin-tab-contracts"],
              ["anagrafica", "Anagrafica Clienti", "admin-tab-anagrafica"],
              ["legal", "Contratti & Legal", "admin-tab-legal"],
              ["plans", "Piani & Prezzi", "admin-tab-plans"],
              ["divisions", "Divisioni ONLINE", "admin-tab-divisions"],
              ["media", "Immagini & Media", "admin-tab-media"],
              ["blog", "Blog & News", "admin-tab-blog"],
              ["features", "Sezioni Sito", "admin-tab-features"],
              ["payments-setup", "Setup Pagamenti", "admin-tab-payments-setup"],
              ["reports", "Rapporti Intervento", "admin-tab-reports"],
              ["safety", "Attestati Sicurezza", "admin-tab-safety"],
              ["team", "Team Tecnici", "admin-tab-team"],
              ["maintenance", "Manutenzione", "admin-tab-maintenance"],
              ["contacts", "Contatti", TID.adminTabContacts],
              ["smtp", "SMTP", "admin-tab-smtp"],
              ["analytics", "Analytics", "admin-tab-analytics"],
              ["security", "Sicurezza", "admin-tab-security"],
            ];
            // Technicians see ONLY: anagrafica + reports + safety (own) + security (own password)
            const TECH_ALLOWED = new Set(["anagrafica", "reports", "safety", "security"]);
            const visibleTabs = user?.role === "technician"
              ? ALL_TABS.filter(([k]) => TECH_ALLOWED.has(k))
              : ALL_TABS;
            return visibleTabs;
          })().map(([k, l, tid]) => (
            <button key={k} data-testid={tid} onClick={() => setTab(k)}
              className={`font-mono-tech text-[11px] px-4 py-3 border-b-2 -mb-px ${tab === k ? "border-[#ff4b00] text-[#ff4b00]" : "border-transparent text-white hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "cash-flow" && <CashFlow />}
        {tab === "shop" && <AdminShop />}
        {tab === "quotes" && (
          <div className="border border-[#1a1a1a]">
            <table className="w-full text-left font-mono-tech text-[11px]">
              <thead className="bg-[#0a0a0a] text-white">
                <tr><th className="p-3">Data</th><th className="p-3">Cliente</th><th className="p-3">Materiale</th><th className="p-3">Qtà</th><th className="p-3">Stima €</th><th className="p-3">Stato</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {quotes.length === 0 && (<tr><td colSpan={7} className="p-8 text-center text-white">Nessun preventivo ancora.</td></tr>)}
                {quotes.map((q) => (
                  <tr key={q.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]">
                    <td className="p-3 text-white">{new Date(q.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="p-3">{q.customer.name}<br/><span className="text-white">{q.customer.email}</span></td>
                    <td className="p-3">{q.specs.material_name}<br/><span className="text-white">{q.specs.thickness_mm}mm · {q.specs.width_mm}×{q.specs.height_mm}</span></td>
                    <td className="p-3">{q.specs.quantity}</td>
                    <td className="p-3">{(q.estimate.total_with_vat + q.shipping.cost).toFixed(2)}</td>
                    <td className="p-3"><StatusBadge s={q.status} /></td>
                    <td className="p-3"><button onClick={() => setSelected(q)} className="text-[#ff4b00] hover:underline">Apri →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "materials" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {materials.map((m) => <MaterialEditor key={m.id} m={m} onSaved={loadAll} setBusy={setBusy} busy={busy} />)}
          </div>
        )}

        {tab === "contacts" && (
          <div className="border border-[#1a1a1a]">
            <table className="w-full text-left font-mono-tech text-[11px]">
              <thead className="bg-[#0a0a0a] text-white"><tr><th className="p-3">Data</th><th className="p-3">Nome</th><th className="p-3">Email</th><th className="p-3">Oggetto</th><th className="p-3">Messaggio</th></tr></thead>
              <tbody>
                {contacts.length === 0 && (<tr><td colSpan={5} className="p-8 text-center text-white">Nessuna richiesta ancora.</td></tr>)}
                {contacts.map((c) => (
                  <tr key={c.id} className="border-t border-[#151515]">
                    <td className="p-3 text-white">{new Date(c.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="p-3">{c.name}</td>
                    <td className="p-3 text-white">{c.email}</td>
                    <td className="p-3">{c.subject}</td>
                    <td className="p-3 text-white max-w-md truncate">{c.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "tickets" && <TicketsPanel />}
        {tab === "contracts" && <ContractsPanel />}
        {tab === "plans" && <PlansPanel />}
        {tab === "divisions" && <DivisionsPanel />}
        {tab === "media" && <MediaManager />}
        {tab === "payments-setup" && <PaymentsSetup />}
        {tab === "reports" && <ReportsPanel />}
        {tab === "smtp" && <SmtpPanel />}
        {tab === "analytics" && <AnalyticsPanel />}
        {tab === "legal" && <LegalPanel />}
        {tab === "blog" && <BlogAdminPanel />}
        {tab === "features" && <FeaturesPanel />}
        {tab === "team" && <TeamPanel />}
        {tab === "safety" && <AdminSafety />}
        {tab === "maintenance" && <MaintenancePanel />}
        {tab === "anagrafica" && <AnagraficaPanel />}
        {tab === "security" && <SecurityPanel />}
      </div>

      {selected && <QuoteDrawer q={selected} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); loadAll(); }} />}
    </div>
  );
}

const StatusBadge = ({ s }) => {
  const map = {
    pending_review: ["In revisione", "border-white/30 text-white"],
    approved: ["Approvato", "border-[#ff4b00] text-[#ff4b00]"],
    paid: ["Pagato", "border-[#22c55e] text-[#22c55e]"],
    in_production: ["In produzione", "border-[#0055ff] text-[#0055ff]"],
    shipped: ["Spedito", "border-[#22c55e] text-[#22c55e]"],
    completed: ["Completato", "border-[#22c55e] text-[#22c55e]"],
    rejected: ["Rifiutato", "border-red-400 text-red-400"],
  };
  const [label, cls] = map[s] || [s, "border-white/30 text-white"];
  return <span className={`font-mono-tech text-[10px] px-2 py-1 border ${cls}`}>{label}</span>;
};

function MaterialEditor({ m, onSaved, busy, setBusy }) {
  const [state, setState] = useState({
    price_per_kg: m.price_per_kg,
    cut_price_per_meter: m.cut_price_per_meter,
    setup_fee: m.setup_fee,
    max_thickness_mm: m.max_thickness_mm,
  });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/materials/${m.id}`, {
        price_per_kg: Number(state.price_per_kg),
        cut_price_per_meter: Number(state.cut_price_per_meter),
        setup_fee: Number(state.setup_fee),
        max_thickness_mm: Number(state.max_thickness_mm),
      });
      toast.success(`${m.name} aggiornato`);
      onSaved();
    } catch (e) { toast.error("Errore aggiornamento"); } finally { setBusy(false); }
  };
  return (
    <div className="border border-[#1a1a1a] p-6">
      <div className="font-display text-xl mb-1">{m.name}</div>
      <div className="font-mono-tech text-[10px] text-white mb-6">{m.key}</div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="field-label">€/kg</label><input type="number" step="0.1" className="field-boxed mt-2" value={state.price_per_kg} onChange={(e) => setState({ ...state, price_per_kg: e.target.value })} /></div>
        <div><label className="field-label">€/m taglio</label><input type="number" step="0.1" className="field-boxed mt-2" value={state.cut_price_per_meter} onChange={(e) => setState({ ...state, cut_price_per_meter: e.target.value })} /></div>
        <div><label className="field-label">Setup €</label><input type="number" step="1" className="field-boxed mt-2" value={state.setup_fee} onChange={(e) => setState({ ...state, setup_fee: e.target.value })} /></div>
        <div><label className="field-label">Spessore max (mm)</label><input type="number" step="0.5" className="field-boxed mt-2" value={state.max_thickness_mm} onChange={(e) => setState({ ...state, max_thickness_mm: e.target.value })} /></div>
      </div>
      <div className="mt-6 flex justify-end">
        <button disabled={busy} onClick={save} className="btn-primary">{busy ? <Loader2 size={14} className="animate-spin" /> : "Salva"}</button>
      </div>
    </div>
  );
}

function QuoteDrawer({ q, onClose, onChanged }) {
  const [price, setPrice] = useState(q.estimate.total_with_vat);
  const [ship, setShip] = useState(q.shipping.cost);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/quotes/${q.id}/approve`, { final_price: Number(price), shipping_cost: Number(ship), admin_note: note });
      toast.success("Preventivo approvato. Il cliente può ora pagare.");
      onChanged();
    } catch (e) { toast.error("Errore"); } finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try { await api.post(`/admin/quotes/${q.id}/reject`); toast.success("Rifiutato"); onChanged(); }
    catch { toast.error("Errore"); } finally { setBusy(false); }
  };
  const updateStatus = async (status) => {
    setBusy(true);
    try { await api.post(`/admin/quotes/${q.id}/status`, { status }); toast.success("Stato aggiornato"); onChanged(); }
    catch { toast.error("Errore"); } finally { setBusy(false); }
  };
  const confirmBonifico = async () => {
    if (!window.confirm("Confermi di aver ricevuto il bonifico bancario? Lo stato passerà a 'Pagato'.")) return;
    setBusy(true);
    try { await api.post(`/admin/quotes/${q.id}/confirm-bank-transfer`); toast.success("Bonifico confermato — preventivo pagato"); onChanged(); }
    catch (e) { toast.error(errMsg(e, "Errore")); } finally { setBusy(false); }
  };

  const paymentLink = `${window.location.origin}/preventivo/${q.id}`;
  const copyLink = () => { navigator.clipboard.writeText(paymentLink); toast.success("Link copiato — invialo al cliente"); };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-stretch justify-end" onClick={onClose}>
      <div className="w-full md:w-[640px] bg-[#080808] border-l border-[#1a1a1a] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="num-chip mb-3"><span className="dot" /> Preventivo · {q.id.slice(0, 8).toUpperCase()}</div>
              <div className="font-display text-3xl">{q.customer.name}</div>
              <div className="text-white text-sm mt-1">{q.customer.email} · {q.customer.phone || "—"}</div>
              {q.customer.company && <div className="text-white text-sm">{q.customer.company}</div>}
            </div>
            <button onClick={onClose} className="btn-ghost p-2"><X size={14} /></button>
          </div>

          <div className="mt-8 border border-[#1a1a1a] p-4">
            <div className="font-mono-tech text-[10px] text-white mb-3">Specifiche</div>
            <div className="grid grid-cols-2 gap-y-2 font-mono-tech text-[11px]">
              <div className="text-white">Materiale</div><div>{q.specs.material_name}</div>
              <div className="text-white">Spessore</div><div>{q.specs.thickness_mm} mm</div>
              <div className="text-white">Dimensioni</div><div>{q.specs.width_mm}×{q.specs.height_mm} mm</div>
              <div className="text-white">Quantità</div><div>{q.specs.quantity} pz</div>
              <div className="text-white">Peso stimato</div><div>{q.estimate.total_weight_kg} kg</div>
              <div className="text-white">CAP</div><div>{q.customer.postal_code || "—"}</div>
            </div>
            {q.specs.notes && <div className="mt-4 pt-4 border-t border-[#151515] text-white text-sm"><span className="text-white font-mono-tech text-[10px] block mb-2">Note cliente</span>{q.specs.notes}</div>}
          </div>

          {q.dwg_file && (
            <a href={`${api.defaults.baseURL}/admin/quotes/${q.id}/download`} target="_blank" rel="noreferrer"
              className="mt-4 border border-[#1a1a1a] p-4 flex items-center justify-between hover:border-[#ff4b00]">
              <div><div className="font-mono-tech text-[10px] text-white">File DWG cliente</div><div className="text-sm text-white">{q.dwg_file.filename}</div></div>
              <ExternalLink size={16} />
            </a>
          )}

          <div className="mt-8">
            <div className="font-mono-tech text-[10px] text-white mb-3">Stima automatica</div>
            <div className="border border-[#1a1a1a] p-4 font-mono-tech text-[11px] space-y-2">
              <div className="flex justify-between"><span className="text-white">Materiale</span><span>€ {q.estimate.material_cost.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-white">Taglio</span><span>€ {q.estimate.cut_cost.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-white">Setup</span><span>€ {q.estimate.setup_fee.toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-[#151515] pt-2 mt-2"><span>Totale IVA incl.</span><span>€ {q.estimate.total_with_vat.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-white">Spedizione</span><span>€ {q.shipping.cost.toFixed(2)}</span></div>
            </div>
          </div>

          {(q.status === "pending_review") && (
            <>
              <div className="mt-8">
                <div className="font-mono-tech text-[10px] text-white mb-3">Prezzo definitivo</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="field-label">Prezzo prodotto (IVA incl.)</label><input type="number" step="0.01" className="field-boxed mt-2" value={price} onChange={(e) => setPrice(e.target.value)} /></div>
                  <div><label className="field-label">Spedizione</label><input type="number" step="0.01" className="field-boxed mt-2" value={ship} onChange={(e) => setShip(e.target.value)} /></div>
                </div>
                <label className="field-label mt-4 block">Nota interna (opzionale)</label>
                <textarea className="field-boxed mt-2" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <div className="mt-8 flex gap-3">
                <button disabled={busy} onClick={approve} className="btn-primary flex-1 justify-center"><Check size={14} /> Approva</button>
                <button disabled={busy} onClick={reject} className="btn-ghost flex-1 justify-center border-red-400/40 text-red-400 hover:bg-red-400/5"><X size={14} /> Rifiuta</button>
              </div>
            </>
          )}

          {q.status === "approved" && (
            <div className="mt-8 border border-[#ff4b00]/60 bg-[#ff4b00]/5 p-4">
              <div className="font-mono-tech text-[10px] text-[#ff4b00] mb-3">Approvato — invia il link al cliente</div>
              <div className="font-mono-tech text-[11px] text-white break-all">{paymentLink}</div>
              <button onClick={copyLink} className="btn-ghost mt-4 py-2 text-[10px]"><Copy size={12} /> Copia link</button>
            </div>
          )}

          {q.status === "awaiting_bank_transfer" && (
            <div className="mt-8 border border-[#ff4b00]/60 bg-[#ff4b00]/5 p-4">
              <div className="font-mono-tech text-[10px] text-[#ff4b00] mb-3">◑ In attesa del bonifico bancario</div>
              <div className="text-white text-sm mb-4">
                Il cliente ha scelto il bonifico. Verifica sulla banca l&apos;arrivo dell&apos;importo e conferma qui.
              </div>
              <button
                disabled={busy}
                onClick={confirmBonifico}
                data-testid="admin-confirm-bonifico"
                className="btn-primary py-2 text-[11px]"
              >
                <Check size={12} /> Conferma bonifico ricevuto
              </button>
            </div>
          )}

          {(q.status === "paid" || q.status === "in_production" || q.status === "shipped") && (
            <div className="mt-8">
              <div className="font-mono-tech text-[10px] text-white mb-3">Avanza stato produzione</div>
              <div className="flex flex-wrap gap-2">
                {["in_production", "shipped", "completed"].map((s) => (
                  <button key={s} disabled={busy} onClick={() => updateStatus(s)} className="btn-ghost text-[10px] py-2">{s.replace("_", " ")}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────  DIVISIONS VISIBILITY PANEL  ───────────────── */
const DIVISION_LABELS = {
  irrigazione: "Irrigazione",
  "trattamento-acqua": "Desalinazione & Trattamento Acque",
  automazioni: "Automazioni",
  elettrico: "Impianti Elettrici",
  fotovoltaico: "Fotovoltaico",
  antiincendio: "Antincendio",
};

function DivisionsPanel() {
  const [vis, setVis] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { const r = await api.get("/divisions/settings"); setVis(r.data); };
  useEffect(() => { load(); }, []);
  const toggle = async (key) => {
    const next = { ...vis, [key]: !vis[key] };
    setVis(next);
    setBusy(true);
    try {
      await api.put("/admin/divisions/settings", { [key]: next[key] });
      toast.success(`${DIVISION_LABELS[key]} ${next[key] ? "ONLINE" : "in pausa"}`);
    } catch { toast.error("Errore"); load(); } finally { setBusy(false); }
  };
  if (!vis) return <div className="font-mono-tech text-white">Caricamento...</div>;
  return (
    <div>
      <div className="mb-8 border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
        <div className="font-mono-tech text-white text-[10px] mb-2">◈ Come funziona</div>
        <p className="text-white text-sm">
          Attiva/disattiva le divisioni dalla home page. Le divisioni <span className="text-white">in pausa</span>
          non compaiono nei capitoli in home e non sono accessibili dal menu.
          Le pagine `/divisione/*` restano tecnicamente raggiungibili per il diretto URL — utile in fase di finalizzazione.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(DIVISION_LABELS).map(([key, label]) => (
          <div key={key} className={`border p-6 flex items-center justify-between ${vis[key] ? "border-[#22c55e]/40 bg-[#0a1710]" : "border-[#1a1a1a] bg-[#0a0a0a]"}`}>
            <div>
              <div className="font-display text-2xl">{label}</div>
              <div className={`font-mono-tech text-[10px] mt-2 ${vis[key] ? "text-[#22c55e]" : "text-white"}`}>
                {vis[key] ? "● ONLINE" : "○ IN PAUSA"}
              </div>
            </div>
            <button
              disabled={busy}
              onClick={() => toggle(key)}
              data-testid={`toggle-${key}`}
              aria-label={`Toggle ${label}`}
              className={`w-14 h-8 rounded-full border relative transition-colors ${vis[key] ? "bg-[#22c55e] border-[#22c55e]" : "bg-[#111] border-[#333]"}`}
            >
              <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-transform ${vis[key] ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────  TICKETS PANEL  ───────────────── */
const TICKET_STATUSES = ["new", "assigned", "in_progress", "resolved", "closed"];

function TicketsPanel() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = async () => {
    const r = await api.get("/admin/service/tickets"); setTickets(r.data);
  };
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => {
    await api.put(`/admin/service/tickets/${id}`, patch);
    toast.success("Aggiornato"); load();
    if (selected) setSelected({ ...selected, ...patch });
  };
  return (
    <>
      <div className="border border-[#1a1a1a] mb-4">
        <table className="w-full text-left font-mono-tech text-[11px]">
          <thead className="bg-[#0a0a0a] text-white"><tr><th className="p-3">Data</th><th className="p-3">Cliente</th><th className="p-3">Divisione</th><th className="p-3">Titolo</th><th className="p-3">Priorità</th><th className="p-3">Stato</th><th className="p-3"></th></tr></thead>
          <tbody>
            {tickets.length === 0 && (<tr><td colSpan={7} className="p-8 text-center text-white">Nessun ticket ancora.</td></tr>)}
            {tickets.map((t) => (
              <tr key={t.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]">
                <td className="p-3 text-white">{new Date(t.created_at).toLocaleDateString("it-IT")}</td>
                <td className="p-3">{t.customer_name}<br/><span className="text-white">{t.customer_email}</span></td>
                <td className="p-3 text-white">{t.area}</td>
                <td className="p-3">{t.title}</td>
                <td className="p-3"><span className={`px-2 py-1 border text-[10px] ${t.priority === "urgent" ? "border-red-400 text-red-400" : t.priority === "high" ? "border-[#ff4b00] text-[#ff4b00]" : "border-white/30 text-white"}`}>{t.priority}</span></td>
                <td className="p-3"><span className="px-2 py-1 border border-white/30 text-[10px]">{t.status}</span></td>
                <td className="p-3"><button onClick={() => setSelected(t)} className="text-[#ff4b00] hover:underline">Apri →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-stretch justify-end" onClick={() => setSelected(null)}>
          <div className="w-full md:w-[600px] bg-[#080808] border-l border-[#1a1a1a] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between">
                <div>
                  <div className="num-chip mb-3"><span className="dot" /> Ticket · {selected.id.slice(0, 8).toUpperCase()}</div>
                  <div className="font-display text-2xl">{selected.title}</div>
                  <div className="text-white text-sm mt-1">{selected.customer_name} · {selected.customer_email} · {selected.customer_phone}</div>
                </div>
                <button onClick={() => setSelected(null)} className="btn-ghost p-2"><X size={14} /></button>
              </div>
              <div className="mt-6 border border-[#1a1a1a] p-4">
                <div className="font-mono-tech text-[10px] text-white mb-2">Divisione · Impianto</div>
                <div className="text-white text-sm">{selected.area}{selected.plant_name && ` · ${selected.plant_name}`}</div>
              </div>
              <div className="mt-4 border border-[#1a1a1a] p-4">
                <div className="font-mono-tech text-[10px] text-white mb-2">Descrizione</div>
                <div className="text-white text-sm whitespace-pre-wrap">{selected.description}</div>
              </div>
              <div className="mt-6">
                <label className="field-label">Cambia stato</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {TICKET_STATUSES.map((s) => (
                    <button key={s} onClick={() => update(selected.id, { status: s })}
                      className={`btn-ghost py-2 text-[10px] ${selected.status === s ? "border-[#ff4b00] text-[#ff4b00]" : ""}`}>
                      {s.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-6">
                <label className="field-label">Note di risoluzione</label>
                <textarea rows={4} className="field-boxed mt-2 resize-none"
                  defaultValue={selected.resolution_notes}
                  onBlur={(e) => update(selected.id, { resolution_notes: e.target.value })} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ─────────────────  CONTRACT REQUESTS PANEL  ───────────────── */
function ContractsPanel() {
  const [reqs, setReqs] = useState([]);
  const load = async () => { const r = await api.get("/admin/service/contract-requests"); setReqs(r.data); };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.put(`/admin/service/contract-requests/${id}`, { status });
    toast.success("Aggiornato"); load();
  };
  return (
    <div className="border border-[#1a1a1a]">
      <table className="w-full text-left font-mono-tech text-[11px]">
        <thead className="bg-[#0a0a0a] text-white"><tr><th className="p-3">Data</th><th className="p-3">Cliente</th><th className="p-3">Piano</th><th className="p-3">Divisione</th><th className="p-3">Impianti</th><th className="p-3">Stima €</th><th className="p-3">Stato</th></tr></thead>
        <tbody>
          {reqs.length === 0 && (<tr><td colSpan={7} className="p-8 text-center text-white">Nessuna richiesta ancora.</td></tr>)}
          {reqs.map((c) => (
            <tr key={c.id} className="border-t border-[#151515]">
              <td className="p-3 text-white">{new Date(c.created_at).toLocaleDateString("it-IT")}</td>
              <td className="p-3">{c.customer_name}<br/><span className="text-white">{c.customer_email} · {c.customer_phone}</span></td>
              <td className="p-3">{c.plan_name} ({c.plan_code})</td>
              <td className="p-3 text-white">{c.area_name}</td>
              <td className="p-3">{c.plants_count}</td>
              <td className="p-3">{c.estimated_annual.toFixed(2)}</td>
              <td className="p-3">
                <select value={c.status} onChange={(e) => setStatus(c.id, e.target.value)} className="bg-transparent border border-[#222] text-[10px] px-2 py-1">
                  {["new", "contacted", "quoted", "won", "lost"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────  PLANS PANEL  ───────────────── */
function PlansPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { const r = await api.get("/admin/service/plans"); setData(r.data); };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="font-mono-tech text-white">Caricamento...</div>;
  const savePlan = async (p) => {
    setBusy(true);
    try {
      await api.put(`/admin/service/plans/${p.id}`, { base_annual: Number(p.base_annual), sla_hours: Number(p.sla_hours), coverage_desc: p.coverage_desc });
      toast.success("Piano aggiornato");
    } catch { toast.error("Errore"); } finally { setBusy(false); }
  };
  const saveArea = async (a) => {
    setBusy(true);
    try { await api.put(`/admin/service/areas/${a.id}`, { multiplier: Number(a.multiplier) }); toast.success("Area aggiornata"); }
    catch { toast.error("Errore"); } finally { setBusy(false); }
  };
  const saveHourly = async (h) => {
    setBusy(true);
    try { await api.put(`/admin/service/hourly/${h.id}`, { price_per_hour: Number(h.price_per_hour), notes: h.notes }); toast.success("Rate aggiornata"); }
    catch { toast.error("Errore"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-10">
      <div>
        <div className="font-mono-tech text-white mb-4">◇ Piani annuali</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.plans.map((p, i) => (
            <div key={p.id} className="border border-[#1a1a1a] p-5">
              <div className="font-display text-2xl mb-1">{p.name}</div>
              <div className="font-mono-tech text-white text-[10px] mb-4">{p.code.toUpperCase()}</div>
              <label className="field-label">Prezzo base annuo (€)</label>
              <input type="number" step="10" className="field-boxed mt-2" value={p.base_annual}
                onChange={(e) => setData({ ...data, plans: data.plans.map((x, j) => j === i ? { ...x, base_annual: e.target.value } : x) })} />
              <label className="field-label mt-4 block">SLA (ore)</label>
              <input type="number" className="field-boxed mt-2" value={p.sla_hours}
                onChange={(e) => setData({ ...data, plans: data.plans.map((x, j) => j === i ? { ...x, sla_hours: e.target.value } : x) })} />
              <label className="field-label mt-4 block">Copertura</label>
              <input className="field-boxed mt-2" value={p.coverage_desc}
                onChange={(e) => setData({ ...data, plans: data.plans.map((x, j) => j === i ? { ...x, coverage_desc: e.target.value } : x) })} />
              <button disabled={busy} onClick={() => savePlan(p)} className="btn-primary mt-4 w-full justify-center">Salva</button>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="font-mono-tech text-white mb-4">◈ Moltiplicatori per divisione</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {data.areas.map((a, i) => (
            <div key={a.id} className="border border-[#1a1a1a] p-4">
              <div className="font-mono-tech text-white text-[11px]">{a.name}</div>
              <div className="font-mono-tech text-white text-[10px]">{a.key}</div>
              <div className="flex gap-2 mt-3">
                <input type="number" step="0.05" className="field-boxed flex-1" value={a.multiplier}
                  onChange={(e) => setData({ ...data, areas: data.areas.map((x, j) => j === i ? { ...x, multiplier: e.target.value } : x) })} />
                <button disabled={busy} onClick={() => saveArea(a)} className="btn-ghost py-2 text-[10px]">Salva</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="font-mono-tech text-white mb-4">◐ Tariffe orarie (interventi a tempo)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.hourly.map((h, i) => (
            <div key={h.id} className="border border-[#1a1a1a] p-5">
              <div className="font-display text-xl mb-1">{h.name}</div>
              <label className="field-label mt-3 block">€/ora</label>
              <input type="number" step="1" className="field-boxed mt-2" value={h.price_per_hour}
                onChange={(e) => setData({ ...data, hourly: data.hourly.map((x, j) => j === i ? { ...x, price_per_hour: e.target.value } : x) })} />
              <label className="field-label mt-4 block">Note</label>
              <input className="field-boxed mt-2" value={h.notes || ""}
                onChange={(e) => setData({ ...data, hourly: data.hourly.map((x, j) => j === i ? { ...x, notes: e.target.value } : x) })} />
              <button disabled={busy} onClick={() => saveHourly(h)} className="btn-primary w-full justify-center mt-4">Salva</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────  SMTP PANEL  ───────────────── */
function SmtpPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState("");
  const load = async () => { const r = await api.get("/admin/settings/smtp"); setCfg(r.data); };
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="font-mono-tech text-white">Caricamento...</div>;
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...cfg };
      if (payload.password === "••••••") delete payload.password;
      await api.put("/admin/settings/smtp", payload); toast.success("SMTP salvato"); load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setBusy(false); }
  };
  const testSend = async () => {
    if (!test) return toast.error("Inserisci un'email");
    setBusy(true);
    try {
      const r = await api.post("/admin/settings/smtp/test", { to: test });
      if (r.data.sent) toast.success(`Email inviata a ${test}`);
      else if (r.data.queued) toast.info("SMTP non abilitato, email accodata");
      else toast.error("Invio fallito: " + (r.data.error || "?"));
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="border border-[#1a1a1a] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display text-2xl">Server SMTP</div>
            <div className="font-mono-tech text-[10px] text-white">Per inviare email transazionali (ticket, contratti...)</div>
          </div>
          <Mail size={20} className="text-[#ff4b00]" />
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><label className="field-label">Host</label><input className="field-boxed mt-2" placeholder="smtp.gmail.com" value={cfg.host} onChange={(e) => setCfg({ ...cfg, host: e.target.value })} data-testid="smtp-host" /></div>
            <div><label className="field-label">Porta</label><input type="number" className="field-boxed mt-2" value={cfg.port} onChange={(e) => setCfg({ ...cfg, port: Number(e.target.value) })} data-testid="smtp-port" /></div>
          </div>
          <div><label className="field-label">Username</label><input className="field-boxed mt-2" placeholder="user@dominio.it" value={cfg.username} onChange={(e) => setCfg({ ...cfg, username: e.target.value })} data-testid="smtp-username" /></div>
          <div><label className="field-label">Password</label><input type="password" className="field-boxed mt-2" placeholder={cfg.password === "••••••" ? "••••••" : ""} onChange={(e) => setCfg({ ...cfg, password: e.target.value })} data-testid="smtp-password" /></div>
          <div><label className="field-label">Email mittente</label><input type="email" className="field-boxed mt-2" placeholder="noreply@gefion-evolution.it" value={cfg.from_email} onChange={(e) => setCfg({ ...cfg, from_email: e.target.value })} data-testid="smtp-from-email" /></div>
          <div><label className="field-label">Nome mittente</label><input className="field-boxed mt-2" value={cfg.from_name} onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })} data-testid="smtp-from-name" /></div>
          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 text-white text-sm"><input type="checkbox" checked={cfg.use_tls} onChange={(e) => setCfg({ ...cfg, use_tls: e.target.checked })} /> Usa TLS/STARTTLS</label>
            <label className="flex items-center gap-2 text-white text-sm"><input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="smtp-enabled" /> Abilitato</label>
          </div>
          <button disabled={busy} onClick={save} data-testid="smtp-save" className="btn-primary w-full justify-center mt-2">{busy ? <Loader2 size={14} className="animate-spin" /> : "Salva configurazione"}</button>
        </div>
      </div>
      <div className="border border-[#1a1a1a] p-6">
        <div className="font-display text-2xl mb-2">Test invio</div>
        <div className="font-mono-tech text-[10px] text-white mb-6">Verifica la configurazione inviando un'email di test.</div>
        <label className="field-label">Email destinatario</label>
        <input type="email" className="field-boxed mt-2" placeholder="test@dominio.it" value={test} onChange={(e) => setTest(e.target.value)} data-testid="smtp-test-to" />
        <button disabled={busy} onClick={testSend} data-testid="smtp-test-btn" className="btn-ghost w-full justify-center mt-4"><Send size={14} /> Invia test</button>
        <div className="mt-8 pt-6 border-t border-[#1a1a1a] font-mono-tech text-[10px] text-white">
          💡 <strong>Suggerimenti server</strong><br/>
          Gmail: <span className="text-white">smtp.gmail.com:587</span> (crea App Password)<br/>
          Aruba: <span className="text-white">smtps.aruba.it:465</span> (no TLS)<br/>
          Register.it: <span className="text-white">authsmtp.register.it:587</span>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────  REPORTS PANEL (Rapporti Intervento)  ───────────────── */
const emptyPart = { name: "", qty: 1, unit_price: 0 };

function ReportsPanel() {
  const [reports, setReports] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [v2Editing, setV2Editing] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [r, t] = await Promise.all([
      api.get("/admin/reports"),
      api.get("/admin/service/tickets"),
    ]);
    setReports(r.data); setTickets(t.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = (ticket = null) => {
    setF({
      ticket_id: ticket?.id || "",
      customer_email: ticket?.customer_email || "",
      customer_name: ticket?.customer_name || "",
      plant_name: ticket?.plant_name || "",
      area: ticket?.area || "irrigazione",
      intervention_date: new Date().toISOString().slice(0, 10),
      technician_name: "",
      work_description: "",
      spare_parts: [{ ...emptyPart }],
      hours_worked: 0,
      hourly_rate: 65,
      travel_km: 0,
      travel_rate_per_km: 0.5,
      is_covered_by_contract: false,
      notes: "",
    });
    setCreating(true);
  };

  const save = async () => {
    if (!f.technician_name || !f.work_description) return toast.error("Tecnico e descrizione lavoro obbligatori");
    setBusy(true);
    try {
      const payload = {
        ...f,
        spare_parts: (f.spare_parts || []).filter((sp) => sp.name && sp.qty > 0)
          .map((sp) => ({ name: sp.name, qty: Number(sp.qty), unit_price: Number(sp.unit_price) })),
        hours_worked: Number(f.hours_worked) || 0,
        hourly_rate: Number(f.hourly_rate) || 0,
        travel_km: Number(f.travel_km) || 0,
        travel_rate_per_km: Number(f.travel_rate_per_km) || 0,
      };
      if (!payload.ticket_id) delete payload.ticket_id;
      if (!payload.customer_email) delete payload.customer_email;
      if (!payload.customer_name) delete payload.customer_name;
      if (!payload.plant_name) delete payload.plant_name;
      const r = await api.post("/admin/reports", payload);
      toast.success(`Rapporto creato · totale € ${r.data.totals.gross_total.toFixed(2)}`);
      setCreating(false); load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setBusy(false); }
  };

  const send = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/reports/${id}/send`);
      if (r.data.email_result?.sent) toast.success("Email inviata al cliente");
      else if (r.data.email_result?.queued) toast.info("SMTP non abilitato — email accodata");
      else toast.error("Invio fallito: " + (r.data.email_result?.error || "?"));
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setBusy(false); }
  };

  if (creating && f) return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="font-display text-2xl">Nuovo rapporto intervento</div>
        <button onClick={() => setCreating(false)} className="btn-ghost">Annulla</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="field-label">Collega a ticket (opzionale)</label>
          <select className="field-boxed mt-2" value={f.ticket_id} onChange={(e) => {
            const t = tickets.find((x) => x.id === e.target.value);
            setF({ ...f, ticket_id: e.target.value, customer_email: t?.customer_email || f.customer_email, customer_name: t?.customer_name || f.customer_name, plant_name: t?.plant_name || f.plant_name, area: t?.area || f.area });
          }}>
            <option value="">— Nessuno —</option>
            {tickets.map((t) => <option key={t.id} value={t.id}>{t.id.slice(0, 8).toUpperCase()} · {t.customer_name} · {t.title}</option>)}
          </select>
        </div>
        <div><label className="field-label">Data intervento</label><input type="date" className="field-boxed mt-2" value={f.intervention_date} onChange={(e) => setF({ ...f, intervention_date: e.target.value })} /></div>
        <div><label className="field-label">Tecnico *</label><input data-testid="report-tech" className="field-boxed mt-2" value={f.technician_name} onChange={(e) => setF({ ...f, technician_name: e.target.value })} placeholder="Nome cognome" /></div>
        <div>
          <label className="field-label">Divisione</label>
          <select className="field-boxed mt-2" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })}>
            {["irrigazione", "trattamento-acqua", "automazioni", "elettrico", "fotovoltaico", "antiincendio", "laser"].map((a) => <option key={a}>{a}</option>)}
          </select>
        </div>
        <div><label className="field-label">Cliente</label><input className="field-boxed mt-2" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} /></div>
        <div><label className="field-label">Email cliente</label><input type="email" className="field-boxed mt-2" value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })} /></div>
        <div className="md:col-span-2"><label className="field-label">Impianto / Indirizzo</label><input className="field-boxed mt-2" value={f.plant_name} onChange={(e) => setF({ ...f, plant_name: e.target.value })} /></div>
        <div className="md:col-span-2"><label className="field-label">Lavoro eseguito *</label><textarea data-testid="report-work" rows={5} className="field-boxed mt-2 resize-none" value={f.work_description} onChange={(e) => setF({ ...f, work_description: e.target.value })} placeholder="Descrivi cosa è stato fatto sull'impianto…" /></div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono-tech text-white">◇ Ricambi utilizzati</div>
          <button onClick={() => setF({ ...f, spare_parts: [...f.spare_parts, { ...emptyPart }] })} className="font-mono-tech text-[10px] text-[#ff4b00] hover:underline">+ aggiungi ricambio</button>
        </div>
        <div className="space-y-2">
          {f.spare_parts.map((sp, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <input className="field-boxed col-span-6" placeholder="Nome ricambio" value={sp.name}
                onChange={(e) => setF({ ...f, spare_parts: f.spare_parts.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} />
              <input className="field-boxed col-span-2" type="number" min="0" step="1" placeholder="Q.tà" value={sp.qty}
                onChange={(e) => setF({ ...f, spare_parts: f.spare_parts.map((x, j) => j === i ? { ...x, qty: e.target.value } : x) })} />
              <input className="field-boxed col-span-3" type="number" min="0" step="0.01" placeholder="€ unit." value={sp.unit_price}
                onChange={(e) => setF({ ...f, spare_parts: f.spare_parts.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x) })} />
              <button onClick={() => setF({ ...f, spare_parts: f.spare_parts.filter((_, j) => j !== i) })} className="col-span-1 btn-ghost text-[10px]">✕</button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div><label className="field-label">Ore lavorate</label><input type="number" step="0.5" className="field-boxed mt-2" value={f.hours_worked} onChange={(e) => setF({ ...f, hours_worked: e.target.value })} /></div>
        <div><label className="field-label">€/ora</label><input type="number" step="1" className="field-boxed mt-2" value={f.hourly_rate} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })} /></div>
        <div><label className="field-label">Km trasferta</label><input type="number" step="1" className="field-boxed mt-2" value={f.travel_km} onChange={(e) => setF({ ...f, travel_km: e.target.value })} /></div>
        <div><label className="field-label">€/km</label><input type="number" step="0.05" className="field-boxed mt-2" value={f.travel_rate_per_km} onChange={(e) => setF({ ...f, travel_rate_per_km: e.target.value })} /></div>
      </div>

      <label className="flex items-center gap-3 mt-6 text-white cursor-pointer">
        <input type="checkbox" checked={f.is_covered_by_contract} onChange={(e) => setF({ ...f, is_covered_by_contract: e.target.checked })} className="accent-[#ff4b00]" />
        <span className="text-sm">Intervento coperto da contratto (non fatturato al cliente)</span>
      </label>

      <div><label className="field-label mt-4 block">Note aggiuntive</label><textarea rows={3} className="field-boxed mt-2 resize-none" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>

      <div className="mt-8 flex justify-end gap-3">
        <button onClick={() => setCreating(false)} className="btn-ghost">Annulla</button>
        <button disabled={busy} onClick={save} data-testid="report-save" className="btn-primary">{busy ? "Salvo..." : "Crea rapporto"}</button>
      </div>
    </div>
  );

  return (
    <div>
      {v2Editing ? (
        <AdminReportEditor
          report={v2Editing === "new" ? null : v2Editing}
          onClose={() => { setV2Editing(null); load(); }}
        />
      ) : (
      <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="font-mono-tech text-white">◈ Rapporti di intervento tecnico</div>
        <div className="flex gap-2">
          <button onClick={() => setV2Editing("new")} data-testid="new-report-v2-btn" className="btn-primary">
            <Wrench size={14} /> Nuovo rapporto (V2 · con foto)
          </button>
          <button onClick={() => startNew()} data-testid="new-report-btn" className="btn-ghost text-[11px]">+ Rapporto rapido</button>
        </div>
      </div>
      <div className="border border-[#1a1a1a]">
        <table className="w-full text-left font-mono-tech text-[11px]">
          <thead className="bg-[#0a0a0a] text-white"><tr><th className="p-3">Data</th><th className="p-3">Cliente</th><th className="p-3">Impianto</th><th className="p-3">Tecnico</th><th className="p-3">Totale €</th><th className="p-3">Copertura</th><th className="p-3"></th></tr></thead>
          <tbody>
            {reports.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-white">Nessun rapporto ancora. Clicca su &quot;Nuovo rapporto&quot; per crearne uno.</td></tr>}
            {reports.map((r) => (
              <tr key={r.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]">
                <td className="p-3 text-white">{r.intervention_date}</td>
                <td className="p-3">{r.customer_name}<br/><span className="text-white">{r.customer_email}</span></td>
                <td className="p-3 text-white">{r.plant_name || "—"}<br/><span className="text-white">{r.area}</span></td>
                <td className="p-3">{r.technician_name}</td>
                <td className="p-3">€ {r.totals?.gross_total?.toFixed(2) || "—"}</td>
                <td className="p-3">{r.is_covered_by_contract ? <span className="text-[#22c55e]">In contratto</span> : <span className="text-[#ff4b00]">Fatturabile</span>}</td>
                <td className="p-3">
                  {r.report_number ? (
                    <button onClick={() => setV2Editing(r)} className="text-[#ff4b00] hover:underline mr-3">Modifica V2</button>
                  ) : (
                    <a href={`/rapporto/${r.id}`} target="_blank" rel="noreferrer" className="text-[#ff4b00] hover:underline mr-3">Apri</a>
                  )}
                  <button onClick={() => send(r.id)} className="text-white hover:text-[#ff4b00]">Invia email</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}


// ─── Security Panel: change admin password ───────────────────
// ─── Maintenance Panel: DB cleanup + system utilities (admin-only tab) ─────
function MaintenancePanel() {
  return (
    <div>
      <div className="mb-8">
        <div className="num-chip mb-3"><span className="dot" /> Manutenzione sistema</div>
        <div className="font-display text-3xl md:text-4xl mb-3">Pulizia & manutenzione</div>
        <div className="text-white text-sm max-w-xl">Strumenti di manutenzione riservati all&apos;amministratore. Da qui puoi rimuovere in sicurezza i dati di test che si sono accumulati nel database, oppure fare un reset completo per ripartire da zero.</div>
      </div>
      <CleanupTestDataCard />
      <FactoryResetCard />
    </div>
  );
}


// ─── Factory Reset card: wipe ALL customer requests ────────────
function FactoryResetCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wiping, setWiping] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/system/factory-reset/preview");
      setPreview(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Errore anteprima"));
    } finally { setLoading(false); }
  };

  const executeReset = async () => {
    if (!preview || preview.total === 0) {
      toast.info("Il database è già vuoto");
      return;
    }
    const first = window.prompt(
      `⚠ ATTENZIONE — RESET COMPLETO ⚠\n\n` +
      `Stai per CANCELLARE DEFINITIVAMENTE tutte le ${preview.total} richieste/preventivi/ticket/ordini registrati.\n\n` +
      `Questo azzera SIA i test SIA le richieste reali dei clienti.\n\n` +
      `Vengono conservati: anagrafica clienti, prodotti shop, articoli blog, attestati sicurezza, tecnici, impostazioni.\n\n` +
      `Per confermare, scrivi esattamente:  RESET COMPLETO`
    );
    if (first !== "RESET COMPLETO") { toast.info("Operazione annullata"); return; }
    if (!window.confirm(`Ultima conferma: cancello ${preview.total} elementi. Procedo?`)) return;

    setWiping(true);
    try {
      const r = await api.post("/admin/system/factory-reset?confirm=FACTORY-RESET");
      toast.success(`Reset completato · ${r.data.total} elementi cancellati`);
      setPreview(null);
      await loadPreview();
    } catch (e) {
      toast.error(errMsg(e, "Errore reset"));
    } finally { setWiping(false); }
  };

  const COLLECTION_LABELS = {
    quote_requests: "Richieste preventivo",
    quotes: "Preventivi generati",
    contract_requests: "Richieste contratto",
    contracts: "Contratti generati",
    tickets: "Ticket assistenza",
    orders: "Ordini shop",
    payment_transactions: "Transazioni pagamento",
    intervention_reports: "Rapportini intervento",
    magic_tokens: "Magic link",
    pending_emails: "Email in coda",
    contacts: "Messaggi contatti",
  };

  return (
    <div className="mt-6 border-2 border-[#ef4444]/50 bg-[#ef4444]/[0.04] p-6" data-testid="factory-reset-card">
      <div className="flex items-start gap-3 pb-4 mb-4 border-b border-[#ef4444]/25">
        <div className="w-10 h-10 border border-[#ef4444] bg-[#ef4444]/20 flex items-center justify-center text-[#ef4444]">
          <AlertTriangle size={16} />
        </div>
        <div>
          <div className="font-mono-tech text-[10px] uppercase text-[#ef4444]">◐ Reset completo</div>
          <div className="text-white font-display text-lg mt-0.5">Ripartire da zero</div>
          <div className="text-white text-sm mt-2 max-w-xl">
            Cancella <b>TUTTE</b> le richieste, preventivi, ticket, ordini, rapportini e transazioni — sia test che reali. Ideale per fare il punto zero prima di andare live &quot;pulito&quot;. <b>Non tocca</b>: anagrafica clienti, prodotti, blog, attestati, tecnici, impostazioni.
          </div>
        </div>
      </div>

      {!preview ? (
        <button
          type="button"
          onClick={loadPreview}
          disabled={loading}
          className="btn-ghost"
          data-testid="factory-reset-preview-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Mostra cosa verrà cancellato
        </button>
      ) : (
        <>
          <div className="mb-4">
            {preview.total === 0 ? (
              <div className="flex items-center gap-2 text-[#22c55e] font-mono-tech text-sm">
                <CheckCircle2 size={14} /> Database già vuoto — niente da resettare.
              </div>
            ) : (
              <>
                <div className="font-mono-tech text-[11px] uppercase text-white mb-3">◐ Contenuto attuale</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(preview.counts).filter(([, n]) => n > 0).map(([coll, n]) => (
                    <div key={coll} className="border border-[#1a1a1a] bg-[#0a0a0a] px-3 py-2 flex items-center justify-between">
                      <span className="font-mono-tech text-[10px] uppercase text-white">{COLLECTION_LABELS[coll] || coll}</span>
                      <span className="text-[#ef4444] font-display text-lg" data-testid={`factory-count-${coll}`}>{n}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 font-mono-tech text-[11px] text-white">
                  Totale da cancellare: <span className="text-[#ef4444] font-bold" data-testid="factory-total">{preview.total}</span>
                </div>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadPreview}
              disabled={loading}
              className="btn-ghost"
              data-testid="factory-refresh-btn"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
              Aggiorna
            </button>
            {preview.total > 0 && (
              <button
                type="button"
                onClick={executeReset}
                disabled={wiping}
                className="!bg-[#ef4444] !border-[#ef4444] !text-white hover:!bg-[#dc2626] btn-primary"
                data-testid="factory-execute-btn"
              >
                {wiping ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Reset completo ({preview.total} elementi)
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function SecurityPanel() {
  const { user, logout } = useAuth();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const strength = (pw) => {
    if (!pw) return { label: "", color: "", pct: 0 };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const map = [
      { label: "Molto debole", color: "#ef4444", pct: 15 },
      { label: "Debole", color: "#f97316", pct: 30 },
      { label: "Discreta", color: "#eab308", pct: 55 },
      { label: "Buona", color: "#84cc16", pct: 75 },
      { label: "Forte", color: "#22c55e", pct: 90 },
      { label: "Eccellente", color: "#22c55e", pct: 100 },
    ];
    return map[Math.min(score, 5)];
  };
  const s = strength(newPwd);

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) {
      toast.error("Le due nuove password non coincidono");
      return;
    }
    if (newPwd.length < 8) {
      toast.error("La nuova password deve essere di almeno 8 caratteri");
      return;
    }
    if (!/[A-Za-z]/.test(newPwd) || !/\d/.test(newPwd)) {
      toast.error("La password deve contenere almeno una lettera e un numero");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success("Password aggiornata. Effettua di nuovo il login per sicurezza.");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
      setTimeout(() => { logout(); }, 1500);
    } catch (err) {
      toast.error(errMsg(err, "Errore nel cambio password"));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <div className="num-chip mb-3"><span className="dot" /> Area riservata</div>
        <div className="font-display text-3xl md:text-4xl mb-3">Sicurezza account</div>
        <div className="text-white text-sm max-w-xl">Cambia la password del tuo account amministratore. Dopo l'aggiornamento verrai disconnesso automaticamente e dovrai accedere nuovamente con la nuova password.</div>
      </div>

      <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-6 md:p-8">
        <div className="flex items-center gap-3 pb-6 mb-6 border-b border-[#1a1a1a]">
          <div className="w-10 h-10 border border-[#ff4b00]/30 bg-[#ff4b00]/5 flex items-center justify-center text-[#ff4b00]"><Lock size={16} /></div>
          <div>
            <div className="font-mono-tech text-[10px] text-white">ACCOUNT</div>
            <div className="text-white text-sm">{user?.email}</div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="admin-change-password-form">
          <div>
            <label className="field-label">Password attuale</label>
            <div className="relative mt-3">
              <input
                required
                type={showCurrent ? "text" : "password"}
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                className="field w-full pr-20"
                data-testid="admin-current-password"
                autoComplete="current-password"
              />
              <button type="button" onClick={() => setShowCurrent((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 font-mono-tech text-[10px] text-white hover:text-[#ff4b00]">
                {showCurrent ? "NASCONDI" : "MOSTRA"}
              </button>
            </div>
          </div>

          <div>
            <label className="field-label">Nuova password</label>
            <div className="relative mt-3">
              <input
                required
                type={showNew ? "text" : "password"}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                className="field w-full pr-20"
                data-testid="admin-new-password"
                autoComplete="new-password"
                minLength={8}
              />
              <button type="button" onClick={() => setShowNew((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 font-mono-tech text-[10px] text-white hover:text-[#ff4b00]">
                {showNew ? "NASCONDI" : "MOSTRA"}
              </button>
            </div>
            {newPwd && (
              <div className="mt-3">
                <div className="h-1 bg-[#141414] overflow-hidden">
                  <div className="h-full transition-all duration-300" style={{ width: `${s.pct}%`, background: s.color }} />
                </div>
                <div className="flex justify-between mt-2 font-mono-tech text-[10px]">
                  <span className="text-white">Robustezza</span>
                  <span style={{ color: s.color }}>{s.label}</span>
                </div>
              </div>
            )}
            <div className="mt-3 font-mono-tech text-[10px] text-white space-y-1">
              <div>◐ Minimo 8 caratteri</div>
              <div>◐ Almeno 1 lettera e 1 numero</div>
              <div>◐ Consigliato: maiuscole, minuscole, simboli, 12+ caratteri</div>
            </div>
          </div>

          <div>
            <label className="field-label">Conferma nuova password</label>
            <input
              required
              type={showNew ? "text" : "password"}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              className="field mt-3 w-full"
              data-testid="admin-confirm-password"
              autoComplete="new-password"
              minLength={8}
            />
            {confirmPwd && newPwd !== confirmPwd && (
              <div className="mt-2 font-mono-tech text-[10px] text-[#ef4444]">Le password non coincidono</div>
            )}
          </div>

          <div className="pt-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <button
              type="submit"
              disabled={saving || !currentPwd || !newPwd || newPwd !== confirmPwd}
              data-testid="admin-change-password-submit"
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
              {saving ? "Aggiornamento..." : "Aggiorna password"}
            </button>
            <div className="font-mono-tech text-[10px] text-white">
              Verrai disconnesso automaticamente dopo il salvataggio.
            </div>
          </div>
        </form>
      </div>

      <CleanupTestDataCard />
    </div>
  );
}


// ─── Cleanup test data card (admin-only, inside Sicurezza tab) ─────
function CleanupTestDataCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/system/cleanup-test-data/preview");
      setPreview(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Errore anteprima"));
    } finally { setLoading(false); }
  };

  const executeCleanup = async () => {
    if (!preview || preview.total === 0) {
      toast.info("Nessun dato di test da cancellare");
      return;
    }
    const confirm1 = window.prompt(
      `Stai per CANCELLARE definitivamente ${preview.total} elementi contrassegnati come test.\n\n` +
      `Per confermare, scrivi esattamente:  CANCELLA`
    );
    if (confirm1 !== "CANCELLA") {
      toast.info("Operazione annullata");
      return;
    }
    setDeleting(true);
    try {
      const r = await api.post("/admin/system/cleanup-test-data?confirm=DELETE-TEST-DATA");
      toast.success(`Cancellati ${r.data.total} elementi test`);
      setPreview(null);
      await loadPreview();
    } catch (e) {
      toast.error(errMsg(e, "Errore cancellazione"));
    } finally { setDeleting(false); }
  };

  const COLLECTION_LABELS = {
    customers: "Anagrafica clienti",
    plants: "Impianti",
    tickets: "Ticket assistenza",
    contract_requests: "Richieste contratto",
    quote_requests: "Preventivi laser",
    orders: "Ordini shop",
    intervention_reports: "Rapporti intervento",
    safety_certificates: "Attestati sicurezza",
    magic_tokens: "Magic link token",
    pending_emails: "Email in coda",
  };

  return (
    <div className="mt-6 border border-[#ef4444]/25 bg-[#ef4444]/[0.02] p-6" data-testid="cleanup-test-card">
      <div className="flex items-start gap-3 pb-4 mb-4 border-b border-[#ef4444]/15">
        <div className="w-10 h-10 border border-[#ef4444]/30 bg-[#ef4444]/10 flex items-center justify-center text-[#ef4444]">
          <Trash2 size={16} />
        </div>
        <div>
          <div className="font-mono-tech text-[10px] uppercase text-[#ef4444]">◐ Zona pericolosa</div>
          <div className="text-white font-display text-lg mt-0.5">Pulizia dati test</div>
          <div className="text-white text-sm mt-2 max-w-xl">
            Rimuovi in un&apos;unica operazione tutti i dati di test (email @example.com, nomi/titoli che iniziano con &quot;Test&quot;, &quot;Prova&quot;, &quot;Demo&quot;, &quot;Fake&quot;, ecc.) da ticket, preventivi, contratti, anagrafica, rapportini, ordini e attestati. I dati reali NON vengono toccati.
          </div>
        </div>
      </div>

      {!preview ? (
        <button
          type="button"
          onClick={loadPreview}
          disabled={loading}
          className="btn-ghost"
          data-testid="cleanup-preview-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Anteprima dati da cancellare
        </button>
      ) : (
        <>
          <div className="mb-4">
            {preview.total === 0 ? (
              <div className="flex items-center gap-2 text-[#22c55e] font-mono-tech text-sm">
                <CheckCircle2 size={14} /> Nessun dato di test trovato — il database è già pulito.
              </div>
            ) : (
              <>
                <div className="font-mono-tech text-[11px] uppercase text-white mb-3">◐ Elementi individuati</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(preview.counts).filter(([, n]) => n > 0).map(([coll, n]) => (
                    <div key={coll} className="border border-[#1a1a1a] bg-[#0a0a0a] px-3 py-2 flex items-center justify-between">
                      <span className="font-mono-tech text-[10px] uppercase text-white">{COLLECTION_LABELS[coll] || coll}</span>
                      <span className="text-[#ef4444] font-display text-lg" data-testid={`cleanup-count-${coll}`}>{n}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 font-mono-tech text-[11px] text-white">
                  Totale: <span className="text-[#ef4444] font-bold" data-testid="cleanup-total">{preview.total}</span> elementi
                </div>
                {Object.entries(preview.samples || {}).some(([, s]) => s.length > 0) && (
                  <details className="mt-4">
                    <summary className="cursor-pointer font-mono-tech text-[10px] uppercase text-white hover:text-white">
                      Esempi di ciò che verrà cancellato (primi 5 per tipo)
                    </summary>
                    <div className="mt-2 space-y-2">
                      {Object.entries(preview.samples).map(([coll, items]) => (
                        <div key={coll}>
                          <div className="font-mono-tech text-[10px] uppercase text-white mb-1">{COLLECTION_LABELS[coll] || coll}</div>
                          <ul className="text-xs text-white space-y-0.5 pl-3">
                            {items.map((it) => (
                              <li key={it.id}>· {it.name || "—"} {it.email && <span className="text-white">({it.email})</span>}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadPreview}
              disabled={loading}
              className="btn-ghost"
              data-testid="cleanup-refresh-btn"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
              Aggiorna anteprima
            </button>
            {preview.total > 0 && (
              <button
                type="button"
                onClick={executeCleanup}
                disabled={deleting}
                className="btn-primary !bg-[#ef4444] !border-[#ef4444] hover:!bg-[#dc2626]"
                data-testid="cleanup-execute-btn"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Cancella tutti i test ({preview.total})
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
function AnalyticsPanel() {
  const [ga4Id, setGa4Id] = useState("");
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings/analytics");
      setCurrent(r.data?.ga4_measurement_id || null);
      setGa4Id(r.data?.ga4_measurement_id || "");
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const clean = ga4Id.trim().toUpperCase();
      const r = await api.put("/admin/settings/analytics", {
        ga4_measurement_id: clean || null,
      });
      setCurrent(r.data?.ga4_measurement_id || null);
      setGa4Id(r.data?.ga4_measurement_id || "");
      toast.success(clean ? "GA4 configurato — attivo dopo il consenso cookie" : "Tracciamento GA4 disattivato");
    } catch (err) {
      toast.error(errMsg(err, "Errore nel salvataggio"));
    } finally { setSaving(false); }
  };

  const disable = async () => {
    if (!window.confirm("Vuoi disattivare il tracciamento Google Analytics?")) return;
    setGa4Id("");
    setSaving(true);
    try {
      await api.put("/admin/settings/analytics", { ga4_measurement_id: null });
      setCurrent(null);
      toast.success("Tracciamento GA4 disattivato");
    } catch (err) {
      toast.error(errMsg(err, "Errore"));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <div className="num-chip mb-3"><span className="dot" /> Statistiche visitatori</div>
        <div className="font-display text-3xl md:text-4xl mb-3">Google Analytics 4</div>
        <div className="text-white text-sm max-w-xl">Monitora visite, pagine viste, provenienza e comportamento dei visitatori del sito. Il tracciamento parte automaticamente solo dopo che l&apos;utente accetta i cookie di analisi (conforme GDPR).</div>
      </div>

      {/* Guida rapida */}
      <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 border border-[#ff4b00]/30 bg-[#ff4b00]/5 flex items-center justify-center text-[#ff4b00] font-mono-tech text-[10px]">?</div>
          <div className="font-mono-tech text-[11px] text-white">COME OTTENERE IL MEASUREMENT ID</div>
        </div>
        <ol className="space-y-3 text-sm text-white pl-2">
          <li><span className="text-[#ff4b00] font-mono-tech text-[10px] mr-2">01</span> Vai su <a href="https://analytics.google.com" target="_blank" rel="noreferrer" className="text-[#ff4b00] hover:underline inline-flex items-center gap-1">analytics.google.com <ExternalLink size={11}/></a> e accedi con il tuo account Google.</li>
          <li><span className="text-[#ff4b00] font-mono-tech text-[10px] mr-2">02</span> Crea un nuovo <b>Account</b> (es. &quot;Gefion Evolution&quot;) e una nuova <b>Proprietà</b> GA4.</li>
          <li><span className="text-[#ff4b00] font-mono-tech text-[10px] mr-2">03</span> Aggiungi un <b>Flusso di dati Web</b> con URL <span className="font-mono-tech text-[11px] text-white">https://www.gefion-evolution.it</span></li>
          <li><span className="text-[#ff4b00] font-mono-tech text-[10px] mr-2">04</span> Copia il <b>ID misurazione</b> (formato <span className="font-mono-tech text-[11px] text-[#ff4b00]">G-XXXXXXXXXX</span>) e incollalo qui sotto.</li>
          <li><span className="text-[#ff4b00] font-mono-tech text-[10px] mr-2">05</span> <b>Consigliato</b>: in GA4 → Amministratore → Flussi di dati → Web → Impostazioni avanzate → disattiva <b>&quot;Modifiche pagina basate su eventi cronologia del browser&quot;</b> per evitare doppi conteggi (il sito gestisce già il tracking SPA).</li>
        </ol>
      </div>

      {/* Form */}
      <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-6 md:p-8">
        <div className="flex items-center gap-3 pb-6 mb-6 border-b border-[#1a1a1a]">
          <div className="w-10 h-10 border border-[#ff4b00]/30 bg-[#ff4b00]/5 flex items-center justify-center text-[#ff4b00]"><BarChart3 size={16} /></div>
          <div>
            <div className="font-mono-tech text-[10px] text-white">STATO</div>
            <div className="text-white text-sm">
              {loading ? "Caricamento..." : current ? (
                <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse"/> Attivo — <span className="font-mono-tech text-[11px] text-[#ff4b00]">{current}</span></span>
              ) : (
                <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/30"/> Non configurato</span>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={save} className="space-y-5" data-testid="admin-analytics-form">
          <div>
            <label className="field-label">Measurement ID GA4</label>
            <input
              type="text"
              value={ga4Id}
              onChange={(e) => setGa4Id(e.target.value)}
              placeholder="G-XXXXXXXXXX"
              className="field mt-3 w-full font-mono-tech uppercase"
              data-testid="admin-ga4-id"
              autoComplete="off"
              spellCheck={false}
            />
            <div className="mt-2 font-mono-tech text-[10px] text-white">
              Formato: <span className="text-[#ff4b00]">G-</span> seguito da 10 caratteri alfanumerici maiuscoli. Lascia vuoto per disattivare.
            </div>
          </div>

          <div className="pt-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <button
              type="submit"
              disabled={saving || loading}
              data-testid="admin-analytics-save"
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {saving ? "Salvataggio..." : "Salva"}
            </button>
            {current && (
              <button
                type="button"
                onClick={disable}
                disabled={saving}
                data-testid="admin-analytics-disable"
                className="btn-ghost text-white hover:text-[#ef4444]"
              >
                <X size={14} /> Disattiva tracciamento
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Info privacy */}
      <div className="mt-6 border border-[#1a1a1a] bg-[#0a0a0a]/50 p-5">
        <div className="font-mono-tech text-[10px] text-white mb-3">◐ CONFORMITÀ GDPR</div>
        <div className="text-white text-sm leading-relaxed">
          Il tracciamento si attiva <b className="text-white">solo</b> dopo che il visitatore accetta i cookie di analisi tramite il banner. Fino a quel momento nessun dato viene inviato a Google. Ricorda di aggiornare la <a href="/cookie-policy" target="_blank" rel="noreferrer" className="text-[#ff4b00] hover:underline">Cookie Policy</a> menzionando l&apos;uso di GA4.
        </div>
      </div>
    </div>
  );
}


// ─── Legal Panel: contracts list + T&C editor ─────────────
function LegalPanel() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [legal, setLegal] = useState({ company_vat: "", company_pec: "", terms_html: "", using_default_terms: true });
  const [saving, setSaving] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cs, ls] = await Promise.all([
        api.get("/admin/contracts"),
        api.get("/admin/settings/legal"),
      ]);
      setContracts(cs.data || []);
      setLegal(ls.data);
    } catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async (contractId) => {
    try {
      const r = await api.get(`/admin/contracts/${contractId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contratto-${contractId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch (e) { toast.error(errMsg(e, "Errore nel download PDF")); }
  };

  const previewPdf = (contractId) => {
    api.get(`/admin/contracts/${contractId}/pdf`, { responseType: "blob" })
      .then((r) => {
        const blobUrl = window.URL.createObjectURL(r.data);
        window.open(blobUrl, "_blank");
      })
      .catch((e) => toast.error(errMsg(e, "Errore anteprima")));
  };

  const saveLegal = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings/legal", {
        company_vat: legal.company_vat || null,
        company_pec: legal.company_pec || null,
        terms_html: legal.terms_html || null,
      });
      toast.success("Impostazioni legali salvate");
      await load();
    } catch (e) { toast.error(errMsg(e, "Errore salvataggio")); }
    finally { setSaving(false); }
  };

  const resetTerms = async () => {
    if (!window.confirm("Ripristinare il testo T&C predefinito? Le tue modifiche verranno perse.")) return;
    try {
      await api.post("/admin/settings/legal/reset-terms");
      toast.success("Testo T&C ripristinato al default");
      await load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  return (
    <div className="space-y-10">
      {/* Contratti Attivi */}
      <div>
        <div className="mb-6">
          <div className="num-chip mb-3"><span className="dot" /> Contratti firmati</div>
          <div className="font-display text-3xl md:text-4xl">Contratti Attivi</div>
          <div className="text-white text-sm mt-2">Scarica il PDF di ogni contratto per condividerlo con il cliente o archiviarlo.</div>
        </div>

        {loading ? (
          <div className="font-mono-tech text-white py-8 text-center flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Caricamento...</div>
        ) : contracts.length === 0 ? (
          <div className="border border-dashed border-[#222] p-12 text-center bg-[#0a0a0a]/50">
            <div className="font-display text-2xl text-white mb-2">Nessun contratto attivo</div>
            <div className="font-mono-tech text-[11px] text-white">Crea un contratto dalle richieste ricevute nella tab &quot;Richieste Contratto&quot;.</div>
          </div>
        ) : (
          <div className="border border-[#1a1a1a] overflow-x-auto">
            <table className="w-full text-left font-mono-tech text-[11px] min-w-[900px]" data-testid="admin-contracts-table">
              <thead className="bg-[#0a0a0a] text-white">
                <tr>
                  <th className="p-3">N. Contratto</th>
                  <th className="p-3">Cliente</th>
                  <th className="p-3">Piano</th>
                  <th className="p-3">Divisione</th>
                  <th className="p-3">Impianti</th>
                  <th className="p-3">Periodo</th>
                  <th className="p-3">Canone</th>
                  <th className="p-3">Stato</th>
                  <th className="p-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((c) => (
                  <tr key={c.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]" data-testid={`contract-row-${c.id}`}>
                    <td className="p-3 text-[#ff4b00] font-bold">GEF-{(c.created_at || "").substring(0, 4)}-{c.id.substring(0, 8).toUpperCase()}</td>
                    <td className="p-3 text-white">{c.customer_name}<div className="text-white text-[10px]">{c.customer_email}</div></td>
                    <td className="p-3 text-white"><span className="px-2 py-0.5 bg-[#0a0a0a] border border-[#333] uppercase text-[10px]">{c.plan_code}</span></td>
                    <td className="p-3 text-white">{c.area_key}</td>
                    <td className="p-3 text-white">{c.plants_count}</td>
                    <td className="p-3 text-white text-[10px]">{c.starts_on}<br/>→ {c.ends_on}</td>
                    <td className="p-3 text-white font-bold">€ {Number(c.annual_price || 0).toLocaleString("it-IT")}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 border text-[10px] ${c.status === "active" ? "border-[#22c55e] text-[#22c55e]" : "border-white/20 text-white"}`}>{c.status}</span></td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => previewPdf(c.id)} data-testid={`contract-preview-${c.id}`} title="Anteprima PDF" className="p-1.5 text-white hover:text-[#ff4b00] hover:bg-[#0a0a0a] border border-[#1a1a1a]"><ExternalLink size={12} /></button>
                        <button onClick={() => downloadPdf(c.id)} data-testid={`contract-download-${c.id}`} title="Scarica PDF" className="p-1.5 text-white hover:text-[#ff4b00] hover:bg-[#0a0a0a] border border-[#1a1a1a]"><Download size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legal Settings */}
      <div className="border-t border-[#1a1a1a] pt-10">
        <div className="mb-6">
          <div className="num-chip mb-3"><span className="dot" /> Configurazione documento</div>
          <div className="font-display text-3xl md:text-4xl">Dati Azienda &amp; T&amp;C</div>
          <div className="text-white text-sm mt-2">Modifica i dati aziendali e il testo dei Termini &amp; Condizioni che compaiono in fondo a ogni contratto PDF.</div>
        </div>

        <form onSubmit={saveLegal} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="field-label">Partita IVA / Codice Fiscale</label>
              <input
                type="text"
                value={legal.company_vat || ""}
                onChange={(e) => setLegal({ ...legal, company_vat: e.target.value })}
                placeholder="01234567890"
                className="field mt-3 w-full font-mono-tech"
                data-testid="legal-vat"
              />
            </div>
            <div>
              <label className="field-label">PEC aziendale</label>
              <input
                type="email"
                value={legal.company_pec || ""}
                onChange={(e) => setLegal({ ...legal, company_pec: e.target.value })}
                placeholder="gefion@pec.it"
                className="field mt-3 w-full font-mono-tech"
                data-testid="legal-pec"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="field-label flex items-center gap-2">
                <Scale size={12} /> Termini &amp; Condizioni (HTML)
              </label>
              <div className="flex items-center gap-3">
                {legal.using_default_terms && (
                  <span className="font-mono-tech text-[10px] text-[#22c55e]">◐ TESTO PREDEFINITO ATTIVO</span>
                )}
                {!legal.using_default_terms && (
                  <button type="button" onClick={resetTerms} data-testid="legal-reset-terms" className="font-mono-tech text-[10px] text-white hover:text-[#ff4b00] flex items-center gap-1">
                    <RotateCcw size={10} /> Ripristina default
                  </button>
                )}
                <button type="button" onClick={() => setTermsExpanded(!termsExpanded)} className="font-mono-tech text-[10px] text-white hover:text-[#ff4b00]">
                  {termsExpanded ? "Riduci ↑" : "Espandi ↓"}
                </button>
              </div>
            </div>
            <textarea
              value={legal.terms_html || ""}
              onChange={(e) => setLegal({ ...legal, terms_html: e.target.value, using_default_terms: false })}
              rows={termsExpanded ? 30 : 12}
              className="field w-full font-mono-tech text-[11px] leading-relaxed"
              data-testid="legal-terms-html"
              placeholder="<h3>Art. 1 — ...</h3><p>...</p>"
            />
            <div className="mt-2 font-mono-tech text-[10px] text-white">
              Puoi usare tag HTML: <span className="text-[#ff4b00]">&lt;h3&gt;</span>, <span className="text-[#ff4b00]">&lt;p&gt;</span>, <span className="text-[#ff4b00]">&lt;b&gt;</span>, <span className="text-[#ff4b00]">&lt;ul&gt;</span>, <span className="text-[#ff4b00]">&lt;ol&gt;</span>, <span className="text-[#ff4b00]">&lt;li&gt;</span>. Il testo predefinito include 13 articoli conformi alla normativa italiana (durata, SLA, esclusioni, GDPR, foro Pordenone).
            </div>
          </div>

          <div className="border border-[#ff4b00]/20 bg-[#ff4b00]/5 p-4">
            <div className="font-mono-tech text-[10px] text-[#ff4b00] mb-2">◐ RACCOMANDAZIONE LEGALE</div>
            <div className="text-white text-sm">
              Il testo predefinito è un <b className="text-white">draft standard</b>. Prima dell&apos;uso ufficiale con clienti, sottoponilo al tuo avvocato di fiducia per la validazione. Modifica le clausole in base alle specifiche del tuo settore e alle prassi commerciali.
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <button type="submit" disabled={saving} data-testid="legal-save" className="btn-primary">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {saving ? "Salvataggio..." : "Salva impostazioni"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Blog Admin Panel: list + editor ─────────────────
function BlogAdminPanel() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // post being edited, or "new" for new post

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blog/posts");
      setPosts(r.data);
    } catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (post) => {
    if (!window.confirm(`Eliminare l'articolo "${post.title}"? Operazione irreversibile.`)) return;
    try {
      await api.delete(`/admin/blog/posts/${post.id}`);
      toast.success("Articolo eliminato");
      await load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  if (editing) {
    return (
      <BlogEditor
        post={editing === "new" ? null : editing}
        onClose={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-8 gap-6">
        <div>
          <div className="num-chip mb-3"><span className="dot" /> Content Management</div>
          <div className="font-display text-3xl md:text-4xl">Blog &amp; News</div>
          <div className="text-white text-sm mt-2 max-w-xl">Pubblica articoli e novità aziendali. Ottimo per SEO e per mostrare autorevolezza tecnica ai clienti.</div>
        </div>
        <button onClick={() => setEditing("new")} data-testid="blog-new-post" className="btn-primary shrink-0">
          <Newspaper size={14} /> Nuovo articolo
        </button>
      </div>

      {loading ? (
        <div className="font-mono-tech text-white py-16 text-center flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Caricamento...</div>
      ) : posts.length === 0 ? (
        <div className="border border-dashed border-[#222] p-16 text-center bg-[#0a0a0a]/50">
          <Newspaper size={32} className="text-white mx-auto mb-4" />
          <div className="font-display text-2xl text-white mb-2">Nessun articolo pubblicato</div>
          <div className="font-mono-tech text-[11px] text-white mb-6">Inizia a costruire la tua presenza online. Il primo articolo può essere una presentazione della tua azienda!</div>
          <button onClick={() => setEditing("new")} className="btn-primary inline-flex">Crea il primo articolo <ArrowRightIcon /></button>
        </div>
      ) : (
        <div className="border border-[#1a1a1a] overflow-x-auto">
          <table className="w-full text-left font-mono-tech text-[11px] min-w-[900px]">
            <thead className="bg-[#0a0a0a] text-white">
              <tr>
                <th className="p-3">Titolo</th>
                <th className="p-3">Categoria</th>
                <th className="p-3">Tag</th>
                <th className="p-3">Stato</th>
                <th className="p-3">Views</th>
                <th className="p-3">Aggiornato</th>
                <th className="p-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]" data-testid={`blog-row-${p.id}`}>
                  <td className="p-3 text-white">{p.title}<div className="text-white text-[10px] mt-0.5">/blog/{p.slug}</div></td>
                  <td className="p-3 text-white">{p.category || "—"}</td>
                  <td className="p-3 text-white">{(p.tags || []).slice(0, 3).join(", ") || "—"}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 border text-[10px] ${p.status === "published" ? "border-[#22c55e] text-[#22c55e]" : "border-white/30 text-white"}`}>
                      {p.status === "published" ? "Pubblicato" : "Bozza"}
                    </span>
                  </td>
                  <td className="p-3 text-white">{p.views || 0}</td>
                  <td className="p-3 text-white text-[10px]">{p.updated_at ? new Date(p.updated_at).toLocaleDateString("it-IT") : "—"}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      {p.status === "published" && (
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="Vedi sul sito" className="p-1.5 text-white hover:text-[#ff4b00] border border-[#1a1a1a]"><Eye size={12} /></a>
                      )}
                      <button onClick={() => setEditing(p)} data-testid={`blog-edit-${p.id}`} title="Modifica" className="p-1.5 text-white hover:text-[#ff4b00] border border-[#1a1a1a]"><Edit size={12} /></button>
                      <button onClick={() => remove(p)} data-testid={`blog-delete-${p.id}`} title="Elimina" className="p-1.5 text-white hover:text-red-400 border border-[#1a1a1a]"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const ArrowRightIcon = () => <span className="inline-block ml-1">→</span>;

function BlogEditor({ post, onClose }) {
  const [f, setF] = useState({
    title: post?.title || "",
    slug: post?.slug || "",
    subtitle: post?.subtitle || "",
    cover_image: post?.cover_image || "",
    content_md: post?.content_md || "",
    category: post?.category || "",
    tags: (post?.tags || []).join(", "),
    author: post?.author || "Gefion Evolution",
    seo_title: post?.seo_title || "",
    seo_description: post?.seo_description || "",
    status: post?.status || "draft",
  });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const save = async (publish = false) => {
    if (!f.title.trim() || !f.content_md.trim()) {
      toast.error("Titolo e contenuto sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: f.title.trim(),
        slug: f.slug.trim() || undefined,
        subtitle: f.subtitle,
        cover_image: f.cover_image,
        content_md: f.content_md,
        category: f.category,
        tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
        author: f.author,
        seo_title: f.seo_title,
        seo_description: f.seo_description,
        status: publish ? "published" : f.status,
      };
      if (post) {
        await api.put(`/admin/blog/posts/${post.id}`, payload);
      } else {
        await api.post("/admin/blog/posts", payload);
      }
      toast.success(publish ? "Articolo pubblicato!" : "Articolo salvato");
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Errore nel salvataggio"));
    } finally { setSaving(false); }
  };

  const html = DOMPurify.sanitize(marked.parse(f.content_md || ""));

  return (
    <div>
      <button onClick={onClose} className="font-mono-tech text-[10px] text-white hover:text-[#ff4b00] flex items-center gap-2 mb-6">← Torna alla lista</button>

      <div className="flex items-start justify-between mb-8 gap-6">
        <div>
          <div className="num-chip mb-3"><span className="dot" /> {post ? "Modifica articolo" : "Nuovo articolo"}</div>
          <div className="font-display text-3xl md:text-4xl">{post ? f.title || "Modifica" : "Crea articolo"}</div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => setShowPreview(!showPreview)} className="btn-ghost">
            <Eye size={14} /> {showPreview ? "Editor" : "Anteprima"}
          </button>
          <button onClick={() => save(false)} disabled={saving} data-testid="blog-save-draft" className="btn-ghost">
            {saving ? <Loader2 size={12} className="animate-spin" /> : "Salva bozza"}
          </button>
          <button onClick={() => save(true)} disabled={saving} data-testid="blog-publish" className="btn-primary">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={14} />}
            Pubblica
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main editor */}
        <div className="lg:col-span-2 space-y-5">
          {!showPreview ? (
            <>
              <div>
                <label className="field-label">Titolo *</label>
                <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="field mt-2 w-full text-lg" placeholder="Es. Come mantenere in efficienza un impianto industriale" data-testid="blog-editor-title" />
              </div>

              <div>
                <label className="field-label">Sottotitolo</label>
                <input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} className="field mt-2 w-full" placeholder="Breve descrizione dell'articolo" data-testid="blog-editor-subtitle" />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="field-label">Contenuto *</label>
                  <span className="font-mono-tech text-[10px] text-white">Markdown supportato</span>
                </div>
                <div className="mt-2 border border-[#1a1a1a]">
                  <div className="flex gap-1 border-b border-[#1a1a1a] p-2 bg-[#0a0a0a]">
                    <MdButton onInsert={(s) => setF({ ...f, content_md: f.content_md + s })} label="H2" md="\n\n## " />
                    <MdButton onInsert={(s) => setF({ ...f, content_md: f.content_md + s })} label="H3" md="\n\n### " />
                    <MdButton onInsert={(s) => setF({ ...f, content_md: f.content_md + s })} label="B" md=" **grassetto** " />
                    <MdButton onInsert={(s) => setF({ ...f, content_md: f.content_md + s })} label="I" md=" _corsivo_ " />
                    <MdButton onInsert={(s) => setF({ ...f, content_md: f.content_md + s })} label="Lista" md="\n\n- elemento 1\n- elemento 2\n- elemento 3\n" />
                    <MdButton onInsert={(s) => setF({ ...f, content_md: f.content_md + s })} label="Link" md=" [testo](https://)" />
                    <MdButton onInsert={(s) => setF({ ...f, content_md: f.content_md + s })} label="Img" md="\n\n![descrizione](URL_IMMAGINE)\n" />
                    <MdButton onInsert={(s) => setF({ ...f, content_md: f.content_md + s })} label="Quote" md="\n\n> citazione qui\n" />
                  </div>
                  <textarea
                    required
                    value={f.content_md}
                    onChange={(e) => setF({ ...f, content_md: e.target.value })}
                    rows={22}
                    className="w-full bg-transparent text-white px-4 py-3 font-mono-tech text-[12px] leading-relaxed focus:outline-none resize-y"
                    placeholder="## Introduzione&#10;&#10;Il tuo testo qui...&#10;&#10;- punto 1&#10;- punto 2"
                    data-testid="blog-editor-content"
                  />
                </div>
                <div className="mt-1 font-mono-tech text-[10px] text-white">
                  {f.content_md.split(/\s+/).filter(Boolean).length} parole · ~{Math.max(1, Math.round(f.content_md.split(/\s+/).filter(Boolean).length / 200))} min di lettura
                </div>
              </div>
            </>
          ) : (
            <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-8">
              <div className="font-mono-tech text-[10px] text-white mb-4">◐ ANTEPRIMA CONTENUTO</div>
              {f.title && <div className="font-display text-3xl mb-4">{f.title}</div>}
              {f.subtitle && <div className="italic-serif text-xl text-white mb-6">{f.subtitle}</div>}
              {f.cover_image && <img src={f.cover_image} alt="cover" className="w-full h-64 object-cover mb-6 border border-[#1a1a1a]" />}
              <div className="blog-content" dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-5">
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-5">
            <div className="font-mono-tech text-[10px] text-white mb-3">◐ URL & SLUG</div>
            <label className="field-label">Slug (URL)</label>
            <input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} className="field mt-2 w-full font-mono-tech text-[11px]" placeholder="auto-generato dal titolo" data-testid="blog-editor-slug" />
            {f.slug && <div className="mt-2 font-mono-tech text-[10px] text-white">/blog/{f.slug}</div>}
          </div>

          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-5">
            <div className="font-mono-tech text-[10px] text-white mb-3">◐ IMMAGINE COPERTINA</div>
            <input value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} className="field mt-2 w-full" placeholder="https://..." data-testid="blog-editor-cover" />
            <div className="mt-2 font-mono-tech text-[10px] text-white">Incolla l&apos;URL di un&apos;immagine caricata dal Media Manager o esterna.</div>
            {f.cover_image && (
              <div className="mt-3 border border-[#1a1a1a] overflow-hidden">
                <img src={f.cover_image} alt="preview" className="w-full h-32 object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            )}
          </div>

          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-5 space-y-4">
            <div className="font-mono-tech text-[10px] text-white">◐ ORGANIZZAZIONE</div>
            <div>
              <label className="field-label">Categoria</label>
              <input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="field mt-2 w-full" placeholder="Es. Manutenzione, Novità, Tutorial" data-testid="blog-editor-category" />
            </div>
            <div>
              <label className="field-label">Tag (separati da virgola)</label>
              <input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} className="field mt-2 w-full" placeholder="es. impianti, sicurezza, tutorial" data-testid="blog-editor-tags" />
            </div>
            <div>
              <label className="field-label">Autore</label>
              <input value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} className="field mt-2 w-full" data-testid="blog-editor-author" />
            </div>
          </div>

          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-5 space-y-4">
            <div className="font-mono-tech text-[10px] text-white">◐ SEO</div>
            <div>
              <label className="field-label">Titolo SEO</label>
              <input value={f.seo_title} onChange={(e) => setF({ ...f, seo_title: e.target.value })} className="field mt-2 w-full" placeholder="(opzionale, altrimenti usa il titolo)" data-testid="blog-editor-seo-title" />
              <div className="mt-1 font-mono-tech text-[10px] text-white">{f.seo_title.length}/60</div>
            </div>
            <div>
              <label className="field-label">Meta description</label>
              <textarea value={f.seo_description} onChange={(e) => setF({ ...f, seo_description: e.target.value })} rows={3} className="field mt-2 w-full" placeholder="(consigliato per Google, 120-160 caratteri)" data-testid="blog-editor-seo-desc" />
              <div className="mt-1 font-mono-tech text-[10px] text-white">{f.seo_description.length}/160</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MdButton({ onInsert, label, md }) {
  return (
    <button
      type="button"
      onClick={() => onInsert(md.replace(/\\n/g, "\n"))}
      className="px-2.5 py-1 font-mono-tech text-[10px] text-white hover:text-[#ff4b00] hover:bg-[#141414] border border-[#1a1a1a]"
      title={label}
    >
      {label}
    </button>
  );
}


// ─── Features Panel: toggle visibilità sezioni sito ─────────
function FeaturesPanel() {
  const [features, setFeatures] = useState({ shop_enabled: true, blog_enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // key currently saving

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings/features");
      setFeatures(r.data);
    } catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, value) => {
    setSaving(key);
    try {
      const r = await api.put("/admin/settings/features", { [key]: value });
      setFeatures(r.data);
      toast.success(value ? "Sezione attivata sul sito" : "Sezione disattivata dal sito");
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setSaving(null); }
  };

  const SECTIONS = [
    {
      key: "shop_enabled",
      icon: ShoppingBag,
      title: "Shop E-commerce",
      subtitle: "Negozio online inox & prodotti industriali",
      description: "Se disattivato: il link SHOP scompare dalla nav header, le rotte /shop/* vengono reindirizzate alla home, i prodotti restano gestibili dal pannello admin ma non visibili al pubblico.",
      path: "/shop",
    },
    {
      key: "blog_enabled",
      icon: Newspaper,
      title: "Blog & News",
      subtitle: "Articoli, guide tecniche e novità aziendali",
      description: "Se disattivato: il link BLOG scompare dalla nav header, le rotte /blog e /blog/:slug vengono reindirizzate alla home, gli articoli restano modificabili dal pannello ma non visibili al pubblico.",
      path: "/blog",
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <div className="num-chip mb-3"><span className="dot" /> Visibilità pubblica</div>
        <div className="font-display text-3xl md:text-4xl">Sezioni del Sito</div>
        <div className="text-white text-sm mt-2 max-w-2xl">Attiva o disattiva le sezioni principali del sito pubblico. Utile per lanci graduali, manutenzione temporanea o test A/B.</div>
      </div>

      {loading ? (
        <div className="font-mono-tech text-white py-16 text-center flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Caricamento...</div>
      ) : (
        <div className="space-y-4" data-testid="features-list">
          {SECTIONS.map((s) => {
            const active = features[s.key] !== false;
            const Icon = s.icon;
            const isSaving = saving === s.key;
            return (
              <div key={s.key} className={`border p-6 transition-colors ${active ? "border-[#22c55e]/30 bg-[#22c55e]/5" : "border-[#1a1a1a] bg-[#0a0a0a]"}`} data-testid={`feature-card-${s.key}`}>
                <div className="flex items-start gap-5">
                  <div className={`w-12 h-12 border flex items-center justify-center shrink-0 ${active ? "border-[#22c55e]/40 text-[#22c55e]" : "border-[#333] text-white"}`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="font-display text-2xl text-white">{s.title}</div>
                      <span className={`px-2 py-0.5 border font-mono-tech text-[10px] uppercase ${active ? "border-[#22c55e] text-[#22c55e]" : "border-white/30 text-white"}`}>
                        {active ? "◐ Attiva" : "◐ Disattivata"}
                      </span>
                      {active && (
                        <a href={s.path} target="_blank" rel="noreferrer" className="font-mono-tech text-[10px] text-white hover:text-[#ff4b00] flex items-center gap-1">
                          Vedi sul sito <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                    <div className="font-mono-tech text-white text-[11px] mt-1">{s.subtitle}</div>
                    <div className="text-white text-sm mt-3 leading-relaxed">{s.description}</div>
                  </div>
                  <button
                    onClick={() => toggle(s.key, !active)}
                    disabled={isSaving}
                    data-testid={`feature-toggle-${s.key}`}
                    aria-label={active ? `Disattiva ${s.title}` : `Attiva ${s.title}`}
                    className={`relative w-14 h-8 rounded-full border transition-colors shrink-0 ${active ? "bg-[#22c55e] border-[#22c55e]" : "bg-[#111] border-[#333]"} ${isSaving ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
                  >
                    {isSaving ? (
                      <Loader2 size={12} className="animate-spin absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white" />
                    ) : (
                      <span className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${active ? "translate-x-7" : "translate-x-1"}`} />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 border border-[#1a1a1a] bg-[#0a0a0a]/50 p-5">
        <div className="flex items-start gap-3">
          <Power size={16} className="text-[#ff4b00] mt-0.5 shrink-0" />
          <div>
            <div className="font-mono-tech text-[10px] text-[#ff4b00] mb-2">◐ COME FUNZIONA</div>
            <div className="text-white text-sm leading-relaxed">
              Le modifiche sono <b className="text-white">immediate</b> — non serve fare deploy. Il sito pubblico rileva i cambiamenti al successivo caricamento pagina. Se un visitatore ha una tab già aperta, le modifiche vengono applicate al prossimo refresh.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Team Tecnici: gestione account tecnici ────────────
function TeamPanel() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ email: "", name: "", phone: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/team"); setTeam(r.data || []); }
    catch (e) { toast.error("Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/team/technician", f);
      toast.success(`Tecnico ${f.name} creato — condividi credenziali: ${f.email} / ${f.password}`);
      setF({ email: "", name: "", phone: "", password: "" });
      setCreating(false);
      await load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminare l'account tecnico "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/team/technician/${t.id}`);
      toast.success("Tecnico eliminato");
      await load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const resetPwd = async (t) => {
    const newPwd = window.prompt(`Nuova password per ${t.name} (min 8 caratteri, almeno 1 lettera e 1 numero):`);
    if (!newPwd || newPwd.length < 8) return;
    try {
      await api.post(`/admin/team/technician/${t.id}/reset-password`, { new_password: newPwd });
      toast.success(`Password aggiornata. Condividi: ${t.email} / ${newPwd}`);
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-8 gap-6 flex-wrap">
        <div>
          <div className="num-chip mb-3"><span className="dot" /> Team operativo</div>
          <div className="font-display text-3xl md:text-4xl">Tecnici</div>
          <div className="text-white text-sm mt-2 max-w-xl">Account per i tuoi tecnici sul campo. Accedono solo ad <b className="text-white">Anagrafica Clienti</b>, <b className="text-white">Rapporti Intervento</b> e <b className="text-white">Sicurezza</b> (cambio password propria).</div>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="team-new-tech"><Plus size={14} /> Nuovo tecnico</button>
      </div>

      {creating && (
        <form onSubmit={create} className="border border-[#ff4b00]/30 bg-[#ff4b00]/5 p-6 mb-6">
          <div className="font-mono-tech text-[10px] text-[#ff4b00] mb-4">◐ NUOVO ACCOUNT TECNICO</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="field-label">Nome e cognome *</label><input required value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} className="field mt-2 w-full" data-testid="team-name" /></div>
            <div><label className="field-label">Email *</label><input required type="email" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} className="field mt-2 w-full" data-testid="team-email" placeholder="tecnico@gefion-evolution.it" /></div>
            <div><label className="field-label">Telefono</label><input value={f.phone} onChange={(e)=>setF({...f,phone:e.target.value})} className="field mt-2 w-full" /></div>
            <div><label className="field-label">Password iniziale *</label><input required type="text" value={f.password} onChange={(e)=>setF({...f,password:e.target.value})} className="field mt-2 w-full font-mono-tech" data-testid="team-password" placeholder="min 8 char, 1 lettera + 1 numero" minLength={8} /></div>
          </div>
          <div className="mt-3 font-mono-tech text-[10px] text-white">◐ Comunica la password al tecnico via canale sicuro. Potrà cambiarla dal proprio pannello (tab Sicurezza).</div>
          <div className="mt-5 flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary">{saving ? <Loader2 size={12} className="animate-spin"/> : <Check size={14}/>} Crea account</button>
            <button type="button" onClick={()=>setCreating(false)} className="btn-ghost">Annulla</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-12 font-mono-tech text-white"><Loader2 className="animate-spin inline mr-2" size={14}/> Caricamento...</div>
      ) : team.length === 0 ? (
        <div className="border border-dashed border-[#222] p-12 text-center">
          <Users size={32} className="text-white mx-auto mb-3" />
          <div className="font-display text-2xl text-white mb-2">Nessun tecnico ancora</div>
          <div className="font-mono-tech text-[11px] text-white">Crea il primo account per far accedere il tuo team sul campo.</div>
        </div>
      ) : (
        <div className="border border-[#1a1a1a] overflow-x-auto">
          <table className="w-full text-left font-mono-tech text-[11px]">
            <thead className="bg-[#0a0a0a] text-white"><tr><th className="p-3">Nome</th><th className="p-3">Email</th><th className="p-3">Telefono</th><th className="p-3">Creato</th><th className="p-3 text-right">Azioni</th></tr></thead>
            <tbody>
              {team.map((t) => (
                <tr key={t.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]">
                  <td className="p-3 text-white">{t.name}</td>
                  <td className="p-3 text-white">{t.email}</td>
                  <td className="p-3 text-white">{t.phone || "—"}</td>
                  <td className="p-3 text-white">{t.created_at ? new Date(t.created_at).toLocaleDateString("it-IT") : "—"}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => resetPwd(t)} className="text-white hover:text-[#ff4b00] mr-3">Reset password</button>
                    <button onClick={() => remove(t)} className="text-white hover:text-red-400">Elimina</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

